// Project parameters (V09, V10) and calculation settings. Open questions for Cesar are labelled where a setting depends on one.
import { useConvex, useMutation } from "convex/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import { useProject } from "@/hooks/project";
import { Badge, Button, Card, Checkbox, Field, Input, PageHeader, Select, useRun } from "@/components/ui";
import { ExportButton } from "@/components/Importer";
import { exportActuals, exportBreakdown, exportBudget, exportCalendar, exportChains, exportChangeOrders, exportDeliverables } from "@/lib/formats";
import { sortedPeriods } from "@/shared/model";
import { toExport } from "./Deliverables";
import { BREAKDOWNS } from "@/engine/types";

export const UNCONFIRMED = (
  <Badge tone="amber" title="Cesar hasn't confirmed which option is right; both are built">
    not confirmed
  </Badge>
);

export function SetupPage() {
  const { project, canEdit, isAdmin, b, nodes } = useProject();
  const convex = useConvex();
  const update = useMutation(api.projects.update);
  const remove = useMutation(api.projects.remove);
  const run = useRun();
  const nav = useNavigate();
  const [f, setF] = useState({ name: project.name, client: project.client, manager: project.manager ?? "", codification: project.codification ?? "{PROJECT}-{WBS}-{DOCTYPE}-{ENGCODE}-{SERIAL}" });
  const o = project.options;
  const s = project.settings;
  const setOpt = (k: keyof typeof o, v: boolean) => run(() => update({ projectId: project._id, options: { ...o, [k]: v } }));
  const setSet = (patch: Partial<typeof s>) => run(() => update({ projectId: project._id, settings: { ...s, ...patch } }));
  return (
    <div className="max-w-4xl space-y-4">
      <PageHeader title="Parameters" sub={`${project.code} · ${project.client}`} />
      <Card title="Project" actions={canEdit && <Button variant="primary" onClick={() => run(() => update({ projectId: project._id, ...f }), "Saved")}>Save</Button>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} disabled={!canEdit} />
          </Field>
          <Field label="Client">
            <Input value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} disabled={!canEdit} />
          </Field>
          <Field label="Manager">
            <Input value={f.manager} onChange={(e) => setF({ ...f, manager: e.target.value })} disabled={!canEdit} />
          </Field>
          <Field label="Status">
            <Select value={project.status} disabled={!canEdit} onChange={(e) => run(() => update({ projectId: project._id, status: e.target.value as "active" }))}>
              <option value="active">Active</option>
              <option value="onhold">On hold</option>
              <option value="closed">Closed</option>
            </Select>
          </Field>
          <Field
            label="DLV reference pattern (optional)"
            className="col-span-2"
            hint="Suggests the DLV reference when adding deliverables. Tokens: {PROJECT} {WBS} {DBS} {DOCTYPE} {ENGCODE} {SERIAL}. The contract code is the document-number prefix."
          >
            <Input value={f.codification} onChange={(e) => setF({ ...f, codification: e.target.value })} disabled={!canEdit} />
          </Field>
        </div>
      </Card>

      <Card title="Options">
        <div className="grid grid-cols-2 gap-3">
          <Checkbox label="Single engineering center" hint="Hides OBS pickers on deliverables" checked={o.singleCenter} disabled={!canEdit} onChange={(v) => setOpt("singleCenter", v)} />
          <Checkbox label="No OBS" hint="Hides OBS everywhere" checked={o.noObs} disabled={!canEdit} onChange={(v) => setOpt("noObs", v)} />
          <Checkbox label="No PBS" hint="Hides PBS everywhere" checked={o.noPbs} disabled={!canEdit} onChange={(v) => setOpt("noPbs", v)} />
          <Checkbox label="No WBS" hint="Hides WBS everywhere" checked={o.noWbs} disabled={!canEdit} onChange={(v) => setOpt("noWbs", v)} />
          <Checkbox label="No planned/forecast dates" hint="Progress grid shows earned dates only" checked={o.noPlanForecast} disabled={!canEdit} onChange={(v) => setOpt("noPlanForecast", v)} />
        </div>
      </Card>

      <Card title="Calculation settings">
        <div className="space-y-4">
          <Field label="Labor rate ($ per hour)" hint="Optional, display only. It adds $ next to Budget and VAC on the dashboard and a Cost view (hours × rate) to the reports. It never changes the EVM numbers. Leave it at 0 to hide all $ figures.">
            <Input type="number" min={0} step="0.01" defaultValue={s.laborRate} disabled={!canEdit} className="w-40" onBlur={(e) => Number(e.target.value) !== s.laborRate && setSet({ laborRate: Number(e.target.value) })} />
          </Field>
          <div className="space-y-2">
            <Checkbox
              label="Include indirect hours in EVM (PV, EV, AC, CPI, EAC)"
              hint="Off by default. Indirects stay off the progress curves either way; the progress curve and histogram reports have an Indirect layer checkbox that shows them separately."
              checked={s.includeIndirects}
              disabled={!canEdit}
              onChange={(v) => setSet({ includeIndirects: v })}
            />
            {s.includeIndirects && (
              <Field label={<>Earned value of indirect hours {UNCONFIRMED}</>}>
                <Select className="w-80" value={s.indirectEarned} disabled={!canEdit} onChange={(e) => setSet({ indirectEarned: e.target.value as "actual" })}>
                  <option value="actual">Earned = actual hours spent</option>
                  <option value="planned">Earned = budget spread to date</option>
                </Select>
              </Field>
            )}
          </div>
          <div className="space-y-2">
            <Checkbox
              label="Auto-forecast"
              hint="Locks each deliverable's last milestone date and spreads the unearned in-between milestones from the current cutoff, never passing it."
              checked={s.autoForecast}
              disabled={!canEdit}
              onChange={(v) => setSet({ autoForecast: v })}
            />
            {s.autoForecast && (
              <Field label={<>Spacing {UNCONFIRMED}</>}>
                <Select className="w-80" value={s.autoForecastSpacing} disabled={!canEdit} onChange={(e) => setSet({ autoForecastSpacing: e.target.value as "equal" })}>
                  <option value="equal">Equal gaps</option>
                  <option value="proportional">Same proportions as the planned dates</option>
                </Select>
              </Field>
            )}
          </div>
        </div>
      </Card>

      <Card title="Backup">
        <p className="mb-3 text-sm text-slate-600">
          The backup file holds everything in this project, including closed periods and their frozen numbers. Restore it from the Projects page as a new project (the baseline password isn't included, so a restored project comes back
          unlocked). The Excel export has every table in the import layouts, for reading or re-use.
        </p>
        <div className="flex gap-2">
          <Button
            onClick={() =>
              run(async () => {
                const data = await convex.query(api.backup.exportBackup, { projectId: project._id });
                const url = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: "application/json" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `${project.code}-backup-${new Date().toISOString().slice(0, 10)}.json`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              })
            }
          >
            Download backup (.json)
          </Button>
          <ExportButton
            label="Export everything to Excel"
            file={`${project.code}-all.xlsx`}
            sheets={() => {
              const dById = new Map(b.deliverables.map((d) => [d._id, d.code]));
              return [
                exportDeliverables(project.code, b.deliverables.map(toExport)),
                exportCalendar(sortedPeriods(b.periods)),
                exportChains(b.chains),
                ...BREAKDOWNS.filter((t) => nodes[t].length).map((t) => exportBreakdown(t, nodes[t])),
                exportBudget(b.budgetCells.map((c) => ({ pbs: c.pbs, dbs: c.dbs, wbs: c.wbs, hours: c.hours }))),
                exportChangeOrders(b.changeOrders.map((c) => ({ ...c, deliverableCode: c.deliverableId ? dById.get(c.deliverableId) : undefined }))),
                exportActuals(b.actuals.map((a) => ({ periodNumber: a.periodNumber, codes: a.codes, hours: a.hours, indirect: a.indirect }))),
              ];
            }}
          />
        </div>
      </Card>

      {isAdmin && (
        <Card title="Danger zone">
          <Button
            variant="danger"
            onClick={async () => {
              if (!confirm(`Delete project ${project.code}? It disappears for everyone in your organisation.`)) return;
              const ok = await run(() => remove({ projectId: project._id }), "Project deleted");
              if (ok !== undefined) void nav({ to: "/" });
            }}
          >
            Delete project
          </Button>
        </Card>
      )}
    </div>
  );
}
